/** Shared types for AI company intent questionnaire and express-interest context. */

export type IntentQuestionOption = {
  id: string;
  label: string;
  tags: string[];
};

export type IntentQuestionSelectionMode = "single" | "multiple";

export type IntentQuestionFollowUp = {
  enabled: boolean;
  optional?: boolean;
  type: "textarea";
  prompt: string;
  maxLength: number;
};

export type IntentQuestionAnswerGroupInputType = "catalog_geography";

export type IntentQuestionAnswerGroup = {
  id: string;
  prompt: string;
  selectionMode: IntentQuestionSelectionMode;
  options: IntentQuestionOption[];
  /** Q15 catalog-backed groups — options empty; UI uses platform region/country catalog. */
  inputType?: IntentQuestionAnswerGroupInputType;
  /** Q15 catalog — suggested region ids from profile preferredCollaborationRegions. */
  suggestedRegionIds?: string[];
};

export type IntentConditionalFollowUpType = "textarea" | "multiselect";

export type IntentConditionalFollowUp = {
  id: string;
  triggerOptionIds: string[];
  type: IntentConditionalFollowUpType;
  prompt: string;
  optional: boolean;
  maxLength?: number;
  options?: IntentQuestionOption[];
};

/** Onboarding questionnaire question (v9). Express-interest may omit extended fields at runtime. */
export type IntentQuestion = {
  id: string;
  prompt: string;
  selectionMode?: IntentQuestionSelectionMode;
  /** When set with selectionMode "multiple", caps how many options may be selected (e.g. Q11 = 2). */
  maxSelections?: number;
  decisionSignals?: string[];
  /** Top-level options for standard questions; Q15 also has top-level region-band options alongside answerGroups. */
  options: IntentQuestionOption[];
  followUp?: IntentQuestionFollowUp;
  answerGroups?: IntentQuestionAnswerGroup[];
  conditionalFollowUps?: IntentConditionalFollowUp[];
};

export type IntentQuestionnaireSnapshot = {
  version?: number;
  questions: IntentQuestion[];
};

export type IntentStructuredAnswer = {
  selected?: string | string[];
  followUpText?: string;
  /** Per answerGroup id → selected option id(s). */
  groups?: Record<string, string | string[]>;
  /** Q15 catalog region ids when the company chose regions without countries. */
  regionIds?: string[];
  /** Conditional follow-up id → value(s). */
  conditional?: Record<string, string | string[]>;
};

export type IntentAnswerValue = string | string[] | IntentStructuredAnswer;

/** Per-question answer: legacy string/array or structured object. */
export type IntentAnswers = Record<string, IntentAnswerValue>;

/** Express-interest context answers remain single-select strings. */
export type ExpressInterestAnswers = Record<string, string>;

export type IntentVector = {
  tags: string[];
  facets: Record<string, string>;
};

export type ExpressInterestContextBundle = {
  questions: IntentQuestion[];
  answers: ExpressInterestAnswers;
};

export type ExpressInterestTargetProductService = {
  type: string;
  name: string;
  description: string;
};

export type ExpressInterestSynopsisSections = {
  whoWeAre: string;
  whatWeWantFromYou: string;
  whatWeCanDoTogether: string;
};

export type ExpressInterestSynopsis = {
  title: string;
  summary: string;
  requesterRole: string;
  expectedPartnerRole: string;
  sections?: ExpressInterestSynopsisSections;
};

/** Joint report generated after B submits match 10-Q (visible after B pays). */
export type ShortPathReportDimension = {
  dimension: "Technical" | "Commercial" | "Financial" | "Legal" | "Framing";
  alignment: string;
  gap: string;
};

export type ShortPathReport = {
  promptVersion: string;
  generatedAt: string;
  alignmentByDimension: ShortPathReportDimension[];
  gaps: string[];
  meetingTopics: { label: string; question: string }[];
  collabModels: { title: string; summary: string }[];
  pathForward: string;
  error?: string | null;
};

/** AI-generated collaboration proposal (role pair + pitch) for express-interest selection. */
export type ExpressInterestProposal = {
  id: string;
  title: string;
  summary: string;
  requesterRole: string;
  expectedPartnerRole: string;
};

export const EXPRESS_INTEREST_PROPOSAL_COUNT = 4;
export const EXPRESS_INTEREST_SELECTED_PROPOSAL_COUNT = 2;
/** AI path requires exactly 2. Manual Groq-failure fallback may send 1 card. */
export const EXPRESS_INTEREST_MIN_SELECTED_PROPOSAL_COUNT = 2;
export const EXPRESS_INTEREST_MANUAL_PROPOSAL_ID = "prop_manual";

export const COMPANY_INTENT_QUESTION_COUNT = 15;
export const EXPRESS_INTEREST_CONTEXT_QUESTION_COUNT = 10;
export const RECEIVER_MATCH_QUESTION_COUNT = 10;
export const LEGACY_EXPRESS_INTEREST_CONTEXT_QUESTION_COUNT = 2;
export const INTENT_ADDITIONAL_INFO_MAX_CHARS = 800;
export const INTENT_FOLLOW_UP_MAX_CHARS = 300;

/** One onboarding question with the selected answer label (for reports / PDF). */
export type OnboardingQaItem = {
  prompt: string;
  answer: string;
};

export type CompanyBriefQaItem = {
  questionId: string;
  prompt: string;
  answer: string;
};

export type CompanyBriefSection = {
  id: "who_we_are" | "looking_for";
  title: string;
  items: CompanyBriefQaItem[];
};

export type CompanyBriefPayload = {
  version: number;
  completedAt: string;
  sections: CompanyBriefSection[];
};
